import { useNavigate } from 'react-router-dom';
import { useState } from 'react';

export default function Contact() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // 간단한 유효성 검사
    if (!formData.email || !formData.message) {
      alert('이메일과 문의 내용은 필수 입력 항목입니다.');
      return;
    }

    if (formData.message.length > 500) {
      alert('문의 내용은 500자 이내로 작성해주세요.');
      return;
    }

    setIsSubmitting(true);
    
    try {
      // 실제 제출 로직은 여기에 구현
      // 현재는 시뮬레이션
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      setSubmitStatus('success');
      setFormData({ name: '', email: '', subject: '', message: '' });
      
      setTimeout(() => {
        setSubmitStatus('idle');
      }, 3000);
    } catch (error) {
      setSubmitStatus('error');
      setTimeout(() => {
        setSubmitStatus('idle');
      }, 3000);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  return (
    <div className="min-h-screen bg-[#f5f1e8]">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-sm border-b border-[#e8dfd0] sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <h1 
            onClick={() => navigate('/')}
            className="text-2xl font-light cursor-pointer hover:text-[#6b5d4f] transition-colors duration-200" 
            style={{ fontFamily: 'Georgia, serif', color: '#4a3f33' }}
          >
            게임기획자 해든의 낙서
          </h1>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-6 py-16">
        <div className="bg-white/60 border border-[#e8dfd0] rounded-lg p-8 md:p-12">
          <div className="text-center mb-12">
            <div className="w-16 h-16 bg-gradient-to-br from-[#8b4513] to-[#654321] rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
              <i className="ri-mail-send-line text-3xl text-white"></i>
            </div>
            <h1 className="text-3xl font-light text-[#4a3f33] mb-3" style={{ fontFamily: 'Georgia, serif' }}>
              문의하기
            </h1>
            <p className="text-sm text-[#7a6d5f] leading-relaxed">
              게임 기획에 관한 질문, 협업 제안, 피드백 등<br />
              무엇이든 편하게 문의해주세요
            </p>
          </div>

          {/* Contact Info */}
          <div className="bg-[#faf8f3] border border-[#e8dfd0] rounded-lg p-6 mb-8">
            <h3 className="text-lg font-normal text-[#4a3f33] mb-4" style={{ fontFamily: 'Georgia, serif' }}>
              연락처 정보
            </h3>
            <div className="space-y-3">
              <div className="flex items-center gap-3 text-sm text-[#6b5d4f]">
                <i className="ri-mail-line text-lg text-[#8b4513]"></i>
                <a href="mailto:mgrn93@naver.com" className="hover:text-[#4a3f33] transition-colors duration-200 cursor-pointer">
                  mgrn93@naver.com
                </a>
              </div>
              <div className="flex items-center gap-3 text-sm text-[#6b5d4f]">
                <i className="ri-time-line text-lg text-[#8b4513]"></i>
                <span>평일 10:00 - 18:00 (주말 및 공휴일 제외)</span>
              </div>
              <div className="flex items-center gap-3 text-sm text-[#6b5d4f]">
                <i className="ri-chat-smile-3-line text-lg text-[#8b4513]"></i>
                <span>보통 24시간 이내에 답변드립니다</span>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-[#4a3f33] mb-2">
                이름 또는 닉네임 <span className="text-[#9d8b6f]">(선택)</span>
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="홍길동"
                className="w-full px-4 py-3 bg-white border border-[#e8dfd0] rounded-lg text-[#4a3f33] placeholder-[#9d8b6f] focus:outline-none focus:border-[#8b4513] transition-colors text-sm"
              />
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-[#4a3f33] mb-2">
                이메일 <span className="text-[#8b4513]">*</span>
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="example@email.com"
                required
                className="w-full px-4 py-3 bg-white border border-[#e8dfd0] rounded-lg text-[#4a3f33] placeholder-[#9d8b6f] focus:outline-none focus:border-[#8b4513] transition-colors text-sm"
              />
            </div>

            <div>
              <label htmlFor="subject" className="block text-sm font-medium text-[#4a3f33] mb-2">
                제목 <span className="text-[#9d8b6f]">(선택)</span>
              </label>
              <input
                type="text"
                id="subject"
                name="subject"
                value={formData.subject}
                onChange={handleChange}
                placeholder="문의 제목을 입력하세요"
                className="w-full px-4 py-3 bg-white border border-[#e8dfd0] rounded-lg text-[#4a3f33] placeholder-[#9d8b6f] focus:outline-none focus:border-[#8b4513] transition-colors text-sm"
              />
            </div>

            <div>
              <label htmlFor="message" className="block text-sm font-medium text-[#4a3f33] mb-2">
                문의 내용 <span className="text-[#8b4513]">*</span>
              </label>
              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                placeholder="문의하실 내용을 자세히 작성해주세요 (최대 500자)"
                required
                maxLength={500}
                rows={8}
                className="w-full px-4 py-3 bg-white border border-[#e8dfd0] rounded-lg text-[#4a3f33] placeholder-[#9d8b6f] focus:outline-none focus:border-[#8b4513] transition-colors text-sm resize-none"
              />
              <div className="text-xs text-[#9d8b6f] mt-1 text-right">
                {formData.message.length} / 500자
              </div>
            </div>

            {/* Submit Status */}
            {submitStatus === 'success' && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center gap-3">
                <i className="ri-checkbox-circle-fill text-green-600 text-xl"></i>
                <p className="text-sm text-green-800">
                  문의가 성공적으로 전송되었습니다! 빠른 시일 내에 답변드리겠습니다.
                </p>
              </div>
            )}

            {submitStatus === 'error' && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
                <i className="ri-error-warning-fill text-red-600 text-xl"></i>
                <p className="text-sm text-red-800">
                  문의 전송에 실패했습니다. 잠시 후 다시 시도해주세요.
                </p>
              </div>
            )}

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full px-6 py-4 bg-gradient-to-r from-[#8b4513] to-[#654321] text-white rounded-lg hover:from-[#654321] hover:to-[#4a3010] transition-all duration-300 text-sm font-medium whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed shadow-md"
            >
              {isSubmitting ? (
                <span className="flex items-center justify-center gap-2">
                  <i className="ri-loader-4-line animate-spin"></i>
                  전송 중...
                </span>
              ) : (
                <span className="flex items-center justify-center gap-2">
                  <i className="ri-send-plane-fill"></i>
                  문의 보내기
                </span>
              )}
            </button>
          </form>

          {/* Additional Info */}
          <div className="mt-8 pt-8 border-t border-[#e8dfd0]">
            <h3 className="text-sm font-medium text-[#4a3f33] mb-3">자주 묻는 질문</h3>
            <div className="space-y-3 text-sm text-[#6b5d4f]">
              <div className="flex items-start gap-2">
                <i className="ri-question-line text-[#8b4513] mt-0.5"></i>
                <div>
                  <p className="font-medium text-[#4a3f33]">프리미엄 구독은 어떻게 하나요?</p>
                  <p className="text-xs mt-1">현재 프리미엄 구독 서비스는 준비 중입니다. 출시 시 공지사항을 통해 안내드리겠습니다.</p>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <i className="ri-question-line text-[#8b4513] mt-0.5"></i>
                <div>
                  <p className="font-medium text-[#4a3f33]">게임 아이디어를 제안하고 싶어요</p>
                  <p className="text-xs mt-1">위 문의 양식을 통해 아이디어를 보내주시면 검토 후 답변드리겠습니다.</p>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <i className="ri-question-line text-[#8b4513] mt-0.5"></i>
                <div>
                  <p className="font-medium text-[#4a3f33]">협업 제안은 어떻게 하나요?</p>
                  <p className="text-xs mt-1">협업 관련 문의는 이메일(mgrn93@naver.com)로 직접 연락 주시면 감사하겠습니다.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-[#e8dcc8] py-8 px-6 mt-16">
        <div className="max-w-6xl mx-auto text-center">
          <p className="text-xs text-[#7a6d5f]">© 2026 게임기획자 해든의 낙서</p>
        </div>
      </footer>
    </div>
  );
}
